import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SliderThemeConfig {
  trackColor: string;
  activeTrackColor: string;
  thumbColor: string;
  trackHeight: number;
  thumbSize: number;
  glow?: boolean;
  gradient?: boolean;
  stepped?: boolean;
}

interface SliderProps {
  component: Component;
}

type TickPlacement = 'outside' | 'inside' | 'none';

const MAX_RENDERED_TICKS = 101;
const CHART_DATA_SOURCE_TYPES = new Set([
  'static',
  'query-template',
  'timestamp-aggregate',
  'workflow',
  'composite-query',
  'home-assistant-placeholder',
]);

const readString = (value: unknown, fallback: string): string =>
  typeof value === 'string' ? value : fallback;

const readNumericValue = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const readBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const readOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readOptionalStringOrNumber = (value: unknown): string | number | undefined =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

const readTickPlacement = (value: unknown): TickPlacement => {
  if (value === 'inside' || value === 'outside' || value === 'none') return value;
  return 'outside';
};

const isChartDataSource = (value: unknown): value is ChartDataSource => {
  if (typeof value !== 'object' || value === null || !('type' in value)) return false;
  return typeof value.type === 'string' && CHART_DATA_SOURCE_TYPES.has(value.type);
};

const readDeviceId = (action: unknown): string | undefined => {
  if (typeof action !== 'object' || action === null || !('deviceId' in action)) return undefined;
  const deviceId = action.deviceId;
  return typeof deviceId === 'string' ? deviceId : undefined;
};

const resolveDefaultTickInterval = (range: number): number | null => {
  if (!Number.isFinite(range) || range <= 0) return null;
  const raw = range / 5;
  if (!Number.isFinite(raw) || raw <= 0) return null;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const nice = [1, 2, 5, 10].find(m => m * magnitude >= raw) ?? 10;
  const interval = nice * magnitude;
  return Number.isFinite(interval) && interval > 0 ? interval : null;
};

const resolveTickInterval = (range: number, tickInterval: unknown): number | null => {
  if (!Number.isFinite(range) || range <= 0) return null;
  if (tickInterval !== undefined) {
    return typeof tickInterval === 'number' && Number.isFinite(tickInterval) && tickInterval > 0
      ? tickInterval
      : null;
  }
  return resolveDefaultTickInterval(range);
};

const clampPercentage = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

const getTickKey = (percentage: number): string =>
  `tick-${percentage.toFixed(4)}`;

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, SliderThemeConfig> = {
  flat: {
    trackColor: CONTROL_THEME.muted,
    activeTrackColor: CONTROL_THEME.primary,
    thumbColor: CONTROL_THEME.primary,
    trackHeight: 6,
    thumbSize: 18,
  },
  gradient: {
    trackColor: CONTROL_THEME.muted,
    activeTrackColor: '#0288d1',
    thumbColor: '#0288d1',
    trackHeight: 8,
    thumbSize: 18,
    gradient: true,
  },
  'neon-track': {
    trackColor: '#1a1a2e',
    activeTrackColor: '#00ff88',
    thumbColor: '#00ff88',
    trackHeight: 4,
    thumbSize: 16,
    glow: true,
  },
  minimal: {
    trackColor: CONTROL_THEME.muted,
    activeTrackColor: CONTROL_THEME.mutedForeground,
    thumbColor: CONTROL_THEME.mutedForeground,
    trackHeight: 2,
    thumbSize: 10,
  },
  stepped: {
    trackColor: CONTROL_THEME.muted,
    activeTrackColor: '#6c63ff',
    thumbColor: '#6c63ff',
    trackHeight: 10,
    thumbSize: 18,
    stepped: true,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const Slider: React.FC<SliderProps> = ({ component }) => {
  const properties = component.properties;

  // ─── Property Extraction ─────────────────────────────────────────────
  const themeName = readString(properties.theme, 'flat');
  const min = readNumericValue(properties.min, 0);
  const max = readNumericValue(properties.max, 100);
  const step = readNumericValue(properties.step, 1);
  const safeStep = step > 0 ? step : 1;
  const unit = readString(properties.unit, '');
  const decimals = Math.max(0, Math.min(20, Math.trunc(readNumericValue(properties.decimals, 0))));
  const orientation = readString(properties.orientation, 'horizontal');
  const displayMode = readString(properties.displayMode, 'standard');
  const tickPlacement = readTickPlacement(properties.tickPlacement);
  const showValue = readBoolean(properties.showValue, true);
  const showMinMax = readBoolean(properties.showMinMax, false);
  const showTicks = readBoolean(properties.showTicks, false);
  const label = readString(properties.label, '');
  const disabled = readBoolean(properties.disabled, false);
  const isGaugeMode = displayMode === 'vertical-gauge';
  const isHorizontal = !isGaugeMode && orientation === 'horizontal';

  // ─── Theme Resolution ────────────────────────────────────────────────
  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['flat'];
    return {
      ...base,
      ...(readOptionalString(properties.trackColor) !== undefined && { trackColor: readOptionalString(properties.trackColor) }),
      ...(readOptionalString(properties.activeTrackColor) !== undefined && { activeTrackColor: readOptionalString(properties.activeTrackColor) }),
      ...(readOptionalString(properties.thumbColor) !== undefined && { thumbColor: readOptionalString(properties.thumbColor) }),
    };
  }, [themeName, properties.trackColor, properties.activeTrackColor, properties.thumbColor]);

  // ─── Data Binding ────────────────────────────────────────────────────
  const dataSource = isChartDataSource(properties.dataSource) ? properties.dataSource : undefined;
  const templateId = readOptionalString(properties.queryTemplateId);
  const valueField = readString(properties.valueField, 'value');

  const { value: rawValue } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : readOptionalStringOrNumber(properties.value),
    templateId,
  });

  const propertyValue = readNumericValue(properties.value, 50);
  const effectiveValue = useMemo(() => {
    if (dataSource && rawValue !== null) {
      const parsed = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      if (!isNaN(parsed)) return Math.min(max, Math.max(min, parsed));
    }
    return Math.min(max, Math.max(min, propertyValue));
  }, [dataSource, rawValue, propertyValue, min, max]);

  const [localValue, setLocalValue] = useState(effectiveValue);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) setLocalValue(effectiveValue);
  }, [effectiveValue, isDragging]);

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);
  const canControl = !disabled && !sensorControlBlocked && isViewerMode && Boolean(handlers.onSensorControl);
  const trackRef = useRef<HTMLDivElement>(null);

  const sensorId = useMemo(() => {
    return readDeviceId(properties.action) ?? component.id;
  }, [properties.action, component.id]);

  const snapToStep = useCallback((val: number) => {
    const snapped = Math.round((val - min) / safeStep) * safeStep + min;
    return Math.min(max, Math.max(min, parseFloat(snapped.toFixed(10))));
  }, [min, max, safeStep]);

  const getValueFromPosition = useCallback((clientX: number, clientY: number) => {
    const track = trackRef.current;
    if (!track) return localValue;
    const rect = track.getBoundingClientRect();
    let ratio: number;
    if (isHorizontal) {
      if (!Number.isFinite(clientX) || !Number.isFinite(rect.width) || rect.width <= 0) return localValue;
      ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    } else {
      if (!Number.isFinite(clientY) || !Number.isFinite(rect.height) || rect.height <= 0) return localValue;
      ratio = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
    }
    return snapToStep(min + ratio * (max - min));
  }, [isHorizontal, min, max, snapToStep, localValue]);

  const fireSensorControl = useCallback((value: number) => {
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'set', value },
    });
  }, [handlers, sensorId]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!canControl) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    const newVal = getValueFromPosition(e.clientX, e.clientY);
    setLocalValue(newVal);
  }, [canControl, getValueFromPosition]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const newVal = getValueFromPosition(e.clientX, e.clientY);
    setLocalValue(newVal);
  }, [isDragging, getValueFromPosition]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    fireSensorControl(localValue);
  }, [isDragging, localValue, fireSensorControl]);

  // ─── Computed ────────────────────────────────────────────────────────
  const displayValue = isViewerMode ? localValue : effectiveValue;
  const range = max - min;
  const percentage = range > 0 ? clampPercentage(((displayValue - min) / range) * 100) : 0;
  const displayText = `${displayValue.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;

  // ─── Tick Marks ──────────────────────────────────────────────────────
  const ticks = useMemo(() => {
    if (!showTicks || tickPlacement === 'none' || theme.stepped) return [];
    const interval = resolveTickInterval(range, properties.tickInterval);
    if (interval === null) return [];
    const intervalCount = Math.floor(range / interval);
    if (!Number.isFinite(intervalCount) || intervalCount < 0 || intervalCount + 1 > MAX_RENDERED_TICKS) return [];
    const result = Array.from({ length: intervalCount + 1 }, (_, index) =>
      clampPercentage((index * interval / range) * 100),
    );
    if ((result[result.length - 1] ?? 0) < 100) {
      if (result.length < MAX_RENDERED_TICKS) {
        result.push(100);
      } else if (result.length > 0) {
        result[result.length - 1] = 100;
      }
    }
    return result;
  }, [showTicks, tickPlacement, theme.stepped, range, properties.tickInterval]);

  // ─── Stepped Segments ────────────────────────────────────────────────
  const steppedSegments = useMemo(() => {
    if (!theme.stepped) return null;
    const segCount = Math.round((max - min) / safeStep);
    const clamped = Math.max(2, Math.min(segCount, 50));
    const filledCount = Math.round((percentage / 100) * clamped);
    return { total: clamped, filled: filledCount };
  }, [theme.stepped, min, max, safeStep, percentage]);

  // ─── Active Track Background ─────────────────────────────────────────
  const activeTrackBg = theme.gradient
    ? `linear-gradient(${isHorizontal ? 'to right' : 'to top'}, #4fc3f7, ${theme.activeTrackColor})`
    : theme.activeTrackColor;

  // ─── Render ──────────────────────────────────────────────────────────
  if (isGaugeMode) {
    const gaugeWidth = Math.max(28, theme.trackHeight * 4);
    const gaugePadding = 4;
    const gaugeTickColor = tickPlacement === 'inside' ? CONTROL_THEME.card : CONTROL_THEME.mutedForeground;

    return (
      <div
        data-testid="slider"
        role="slider"
        aria-valuenow={displayValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-orientation="vertical"
        aria-label={label || 'Slider'}
        style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', gap: 8, userSelect: 'none', padding: '8px 6px', boxSizing: 'border-box', minWidth: 0, minHeight: 0 }}
      >
        {label && <span style={{ fontSize: 11, color: CONTROL_THEME.mutedForeground, writingMode: 'vertical-rl', transform: 'rotate(180deg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxHeight: '80%' }}>{label}</span>}

        <div data-testid="slider-gauge" style={{ position: 'relative', height: '100%', minHeight: 48, width: tickPlacement === 'outside' ? gaugeWidth + 18 : gaugeWidth, display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
          {showMinMax && <span style={{ position: 'absolute', top: -2, right: tickPlacement === 'outside' ? 18 : 0, fontSize: 10, color: CONTROL_THEME.mutedForeground }}>{max}</span>}

          <div
            ref={trackRef}
            data-testid="slider-gauge-rail"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              position: 'relative',
              width: gaugeWidth,
              height: '100%',
              borderRadius: 999,
              backgroundColor: theme.trackColor,
              border: `1px solid ${CONTROL_THEME.border}`,
              overflow: 'hidden',
              cursor: canControl ? 'pointer' : 'default',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.18)',
            }}
          >
            <div
              data-testid="slider-gauge-fill"
              style={{
                position: 'absolute',
                left: gaugePadding,
                right: gaugePadding,
                bottom: gaugePadding,
                height: percentage > 0 ? `max(2px, calc(${percentage}% - ${gaugePadding * 2}px))` : 0,
                minHeight: percentage > 0 ? 2 : 0,
                borderRadius: 999,
                background: activeTrackBg,
                ...(theme.glow && { boxShadow: `0 0 10px ${theme.activeTrackColor}60` }),
                transition: isDragging ? 'none' : 'height 0.15s ease',
              }}
            />

            {ticks.length > 0 && tickPlacement === 'inside' && (
              <div data-testid="slider-gauge-ticks-inside" style={{ position: 'absolute', inset: `${gaugePadding}px ${gaugePadding + 2}px`, pointerEvents: 'none' }}>
                {ticks.map((pct) => (
                  <span
                    key={getTickKey(pct)}
                    data-testid="slider-gauge-tick"
                    style={{ position: 'absolute', left: 0, right: 0, top: `${100 - pct}%`, height: 1, backgroundColor: gaugeTickColor, opacity: 0.75, transform: 'translateY(-50%)' }}
                  />
                ))}
              </div>
            )}

            <div
              data-testid="slider-thumb"
              style={{
                position: 'absolute',
                left: '50%',
                bottom: `calc(${percentage}% + ${gaugePadding}px)`,
                width: gaugeWidth - gaugePadding * 3,
                height: 3,
                borderRadius: 999,
                backgroundColor: CONTROL_THEME.card,
                transform: 'translate(-50%, 50%)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.28)',
                transition: isDragging ? 'none' : 'bottom 0.15s ease',
                pointerEvents: 'none',
              }}
            />
          </div>

          {ticks.length > 0 && tickPlacement === 'outside' && (
            <svg data-testid="slider-gauge-ticks-outside" style={{ position: 'absolute', left: gaugeWidth + 4, top: 0, width: 14, height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
              {ticks.map((pct) => (
                <line key={getTickKey(pct)} data-testid="slider-gauge-tick" x1={0} y1={`${100 - pct}%`} x2={8} y2={`${100 - pct}%`} stroke={gaugeTickColor} strokeWidth={1} />
              ))}
            </svg>
          )}

          {showMinMax && <span style={{ position: 'absolute', bottom: -2, right: tickPlacement === 'outside' ? 18 : 0, fontSize: 10, color: CONTROL_THEME.mutedForeground }}>{min}</span>}
        </div>

        {showValue && <span data-testid="slider-value" style={{ fontSize: 13, fontWeight: 600, color: CONTROL_THEME.foreground, fontVariantNumeric: 'tabular-nums', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{displayText}</span>}
      </div>
    );
  }

  if (isHorizontal) {
    return (
      <div
        data-testid="slider"
        role="slider"
        aria-valuenow={displayValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-orientation="horizontal"
        aria-label={label || 'Slider'}
        style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'center', gap: 4, userSelect: 'none', padding: '6px 8px', boxSizing: 'border-box', minWidth: 0, minHeight: 0 }}
      >
        {label && <span style={{ fontSize: 11, color: CONTROL_THEME.mutedForeground, textAlign: 'center', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>}

        {showValue && <span data-testid="slider-value" style={{ fontSize: 13, fontWeight: 600, color: CONTROL_THEME.foreground, textAlign: 'center', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{displayText}</span>}

        {/* Track area */}
        <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', minHeight: Math.max(theme.thumbSize, theme.trackHeight + 16) }}>
          {/* Track */}
          <div
            ref={trackRef}
            data-testid="slider-track"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ position: 'relative', width: '100%', height: theme.trackHeight, borderRadius: theme.trackHeight / 2, backgroundColor: theme.trackColor, cursor: canControl ? 'pointer' : 'default' }}
          >
            {/* Active track / stepped segments */}
            {steppedSegments ? (
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', gap: 2 }}>
                {Array.from({ length: steppedSegments.total }, (_, i) => (
                  <div key={i} style={{ flex: 1, height: '100%', borderRadius: 2, backgroundColor: i < steppedSegments.filled ? theme.activeTrackColor : theme.trackColor, transition: 'background-color 0.1s' }} />
                ))}
              </div>
            ) : (
              <div style={{
                position: 'absolute', top: 0, left: 0, height: '100%', width: `${percentage}%`,
                borderRadius: theme.trackHeight / 2,
                background: activeTrackBg,
                ...(theme.glow && { boxShadow: `0 0 8px ${theme.activeTrackColor}60` }),
                transition: isDragging ? 'none' : 'width 0.15s ease',
              }} />
            )}

            {/* Thumb */}
            <div
              data-testid="slider-thumb"
              style={{
                position: 'absolute',
                top: '50%',
                left: `${percentage}%`,
                width: theme.thumbSize,
                height: theme.thumbSize,
                borderRadius: '50%',
                backgroundColor: theme.thumbColor,
                border: `2px solid ${CONTROL_THEME.card}`,
                transform: 'translate(-50%, -50%)',
                boxShadow: theme.glow ? `0 0 10px ${theme.thumbColor}80` : '0 1px 3px rgba(0,0,0,0.3)',
                transition: isDragging ? 'none' : 'left 0.15s ease',
                pointerEvents: 'none',
              }}
            />
          </div>

          {/* Tick marks */}
          {ticks.length > 0 && (
            <svg
              data-testid={tickPlacement === 'inside' ? 'slider-ticks-inside' : 'slider-ticks-outside'}
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: '100%',
                height: tickPlacement === 'inside' ? Math.max(theme.trackHeight + 8, 12) : 20,
                marginTop: tickPlacement === 'inside' ? -Math.max(theme.trackHeight + 8, 12) / 2 : theme.trackHeight / 2 + 2,
                pointerEvents: 'none',
              }}
            >
              {ticks.map((pct) => (
                <line
                  key={getTickKey(pct)}
                  data-testid="slider-tick"
                  x1={`${pct}%`}
                  y1={tickPlacement === 'inside' ? 2 : 0}
                  x2={`${pct}%`}
                  y2={tickPlacement === 'inside' ? Math.max(theme.trackHeight + 6, 10) : 6}
                  stroke={CONTROL_THEME.mutedForeground}
                  strokeWidth={1}
                />
              ))}
            </svg>
          )}
        </div>

        {/* Min/Max labels */}
        {showMinMax && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: CONTROL_THEME.mutedForeground }}>
            <span>{min}</span>
            <span>{max}</span>
          </div>
        )}
      </div>
    );
  }

  // ─── Vertical Layout ──────────────────────────────────────────────────
  return (
    <div
      data-testid="slider"
      role="slider"
      aria-valuenow={displayValue}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-orientation="vertical"
      aria-label={label || 'Slider'}
      style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', gap: 6, userSelect: 'none', padding: '8px 6px', boxSizing: 'border-box', minWidth: 0, minHeight: 0 }}
    >
      {/* Labels column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        {label && <span style={{ fontSize: 11, color: CONTROL_THEME.mutedForeground, writingMode: 'vertical-rl', transform: 'rotate(180deg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxHeight: '80%' }}>{label}</span>}
      </div>

      {/* Track area */}
      <div style={{ position: 'relative', height: '100%', display: 'flex', justifyContent: 'center', minWidth: Math.max(theme.thumbSize, theme.trackHeight + 16) }}>
        {showMinMax && <span style={{ position: 'absolute', top: -2, fontSize: 10, color: CONTROL_THEME.mutedForeground }}>{max}</span>}

        <div
          ref={trackRef}
          data-testid="slider-track"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ position: 'relative', width: theme.trackHeight, height: '100%', borderRadius: theme.trackHeight / 2, backgroundColor: theme.trackColor, cursor: canControl ? 'pointer' : 'default' }}
        >
          {steppedSegments ? (
            <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column-reverse', gap: 2 }}>
              {Array.from({ length: steppedSegments.total }, (_, i) => (
                <div key={i} style={{ flex: 1, width: '100%', borderRadius: 2, backgroundColor: i < steppedSegments.filled ? theme.activeTrackColor : theme.trackColor, transition: 'background-color 0.1s' }} />
              ))}
            </div>
          ) : (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, width: '100%', height: `${percentage}%`,
              borderRadius: theme.trackHeight / 2,
              background: activeTrackBg,
              ...(theme.glow && { boxShadow: `0 0 8px ${theme.activeTrackColor}60` }),
              transition: isDragging ? 'none' : 'height 0.15s ease',
            }} />
          )}

          <div
            data-testid="slider-thumb"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: `${percentage}%`,
              width: theme.thumbSize,
              height: theme.thumbSize,
              borderRadius: '50%',
              backgroundColor: theme.thumbColor,
              border: `2px solid ${CONTROL_THEME.card}`,
              transform: 'translate(-50%, 50%)',
              boxShadow: theme.glow ? `0 0 10px ${theme.thumbColor}80` : '0 1px 3px rgba(0,0,0,0.3)',
              transition: isDragging ? 'none' : 'bottom 0.15s ease',
              pointerEvents: 'none',
            }}
          />
        </div>

        {showMinMax && <span style={{ position: 'absolute', bottom: -2, fontSize: 10, color: CONTROL_THEME.mutedForeground }}>{min}</span>}

        {ticks.length > 0 && (
          <svg
            data-testid={tickPlacement === 'inside' ? 'slider-ticks-inside' : 'slider-ticks-outside'}
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              width: tickPlacement === 'inside' ? Math.max(theme.trackHeight + 8, 12) : 20,
              height: '100%',
              marginLeft: tickPlacement === 'inside' ? -Math.max(theme.trackHeight + 8, 12) / 2 : theme.trackHeight / 2 + 2,
              pointerEvents: 'none',
            }}
          >
            {ticks.map((pct) => (
              <line
                key={getTickKey(pct)}
                data-testid="slider-tick"
                x1={tickPlacement === 'inside' ? 2 : 0}
                y1={`${100 - pct}%`}
                x2={tickPlacement === 'inside' ? Math.max(theme.trackHeight + 6, 10) : 6}
                y2={`${100 - pct}%`}
                stroke={CONTROL_THEME.mutedForeground}
                strokeWidth={1}
              />
            ))}
          </svg>
        )}
      </div>

      {/* Value */}
      {showValue && <span data-testid="slider-value" style={{ fontSize: 13, fontWeight: 600, color: CONTROL_THEME.foreground, fontVariantNumeric: 'tabular-nums', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{displayText}</span>}
    </div>
  );
};
